import { useMutation as useTanstackMutation } from '@tanstack/react-query';

import queryClient, { emitAppToast } from './queryClient';


/** Generic permission surface (#206): a mutation that nobody handles (no
 * hook-level onError, no call-site onError) still tells the user WHY a 403
 * failed. Handled mutations show their own tailored copy instead — the
 * subscription_locked soft-wall toast stays global in the MutationCache. */
function defaultOnError(error) {
  const response = error?.response;
  if (response?.status === 403 && response?.data?.code !== 'subscription_locked') {
    emitAppToast('You don\'t have permission to do that.');
  }
}


function useBaseMutation(client, params) {
  const { mutateFn, ...restParams } = params;

  const mutateFnDefault = (variables) => {
    return mutateFn({ ...variables, apiClient: client, queryClient });
  };

  // TODO: Think about mutation key
  const mutation = useTanstackMutation({
    mutationFn: mutateFnDefault,
    ...restParams,
  });

  // Wrap mutate so the fallback only kicks in when the call site passes no
  // onError of its own (the cache can't see call-site callbacks, so this is
  // the one place both levels are visible).
  const hasHookHandler = Boolean(restParams.onError);
  const mutate = (variables, options) => {
    const handled = hasHookHandler || Boolean(options?.onError);
    return mutation.mutate(variables, handled ? options : { ...options, onError: defaultOnError });
  };

  return { ...mutation, mutate };
}

export default useBaseMutation;
